/**
 * MaterialDNA Component
 * =====================
 * DARK PREMIUM THEME
 * Elegant visual DNA - NOT a technical list
 * Cinematic, minimal, intentional
 */

import React from 'react';
import { useBlueprint } from '../../context/BlueprintContext';

// Intensity visualization - elegant bars
const IntensityBar = ({ value, max = 5 }) => {
  const numValue = typeof value === 'number' ? value : parseInt(value) || 3;
  const normalizedValue = Math.min(Math.max(numValue, 1), max);
  
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <div
          key={i}
          className={`h-1 w-6 transition-colors ${
            i < normalizedValue 
              ? 'bg-[#D6C1A5]' 
              : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
};

// Tone indicator - visual
const ToneIndicator = ({ tone }) => {
  const toneValue = tone?.toLowerCase() || 'neutral';
  const isWarm = ['warm', 'golden', 'honey', 'amber'].some(t => toneValue.includes(t));
  const isCool = ['cool', 'silver', 'grey', 'blue'].some(t => toneValue.includes(t));
  
  return (
    <div className="flex items-center gap-3">
      <div className={`w-2 h-2 rounded-full ${
        isWarm ? 'bg-amber-400' : isCool ? 'bg-[var(--gold)]' : 'bg-white/40'
      }`} />
      <span className="capitalize text-white/80">{tone || 'Neutral'}</span>
    </div>
  );
};

// Single DNA attribute - elegant row
const DNAAttribute = ({ label, children }) => {
  return (
    <div className="flex items-center justify-between py-4 border-b border-white/5 last:border-0">
      <span className="text-[11px] font-medium tracking-[2px] uppercase text-white/40">
        {label}
      </span>
      <div className="text-right">
        {children}
      </div>
    </div>
  );
};

const MaterialDNA = ({ material }) => {
  const { content, getLogic } = useBlueprint();
  
  // Get DNA labels from config
  const dnaContent = content?.material?.dna || {};
  const dnaLabels = dnaContent.fields || {};
  
  // Get DNA field order from logic config
  const dnaConfig = getLogic('sre.dna', {});
  
  if (!material) return null;
  
  // Extract DNA data from material
  const dna = material.material_dna?.[0] || material.material_dna || {};
  
  // Merge DNA with top-level fields
  const dnaData = {
    color_family: dna.color_family || material.color_family || material.dominant_color,
    tone: dna.tone || material.tone,
    contrast: dna.contrast || material.contrast,
    pattern_type: dna.pattern_type || material.pattern_type || material.pattern,
    visual_intensity: dna.visual_intensity || material.visual_intensity,
    application_role: dna.application_role || material.application_role
  };
  
  // Default labels
  const defaultLabels = {
    color_family: { label: 'Colore' },
    tone: { label: 'Temperatura' },
    contrast: { label: 'Contrasto' },
    pattern_type: { label: 'Pattern' },
    visual_intensity: { label: 'Intensità' },
    application_role: { label: 'Applicazione' }
  };
  
  // Parse applications
  const applications = dnaData.application_role
    ?.split(/[,;]/)
    .map(a => a.trim())
    .filter(Boolean) || [];
  
  return (
    <section 
      className="bg-[#0F0F0E] border border-white/5"
      data-testid="material-dna-panel"
    >
      {/* Header - Minimal */}
      <div className="px-6 py-5 border-b border-white/5">
        <h3 className="text-[11px] font-bold tracking-[3px] uppercase text-[#D6C1A5]">
          {dnaContent.title || 'Material DNA'}
        </h3>
      </div>
      
      {/* DNA Attributes */}
      <div className="px-6">
        {/* Color Family */}
        {dnaData.color_family && (
          <DNAAttribute label={dnaLabels.color_family?.label || defaultLabels.color_family.label}>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-[#D6C1A5]/60" />
              <span className="text-sm text-white/90 capitalize">{dnaData.color_family}</span>
            </div>
          </DNAAttribute>
        )}
        
        {/* Tone */}
        {dnaData.tone && (
          <DNAAttribute label={dnaLabels.tone?.label || defaultLabels.tone.label}>
            <ToneIndicator tone={dnaData.tone} />
          </DNAAttribute>
        )}
        
        {/* Pattern */}
        {dnaData.pattern_type && (
          <DNAAttribute label={dnaLabels.pattern_type?.label || defaultLabels.pattern_type.label}>
            <span className="text-sm text-white/90 capitalize">{dnaData.pattern_type}</span>
          </DNAAttribute>
        )}
        
        {/* Visual Intensity */}
        {(dnaData.visual_intensity || dnaData.contrast) && (
          <DNAAttribute label={dnaLabels.visual_intensity?.label || defaultLabels.visual_intensity.label}>
            <IntensityBar value={dnaData.visual_intensity || dnaData.contrast} />
          </DNAAttribute>
        )}
        
        {/* Applications */}
        {applications.length > 0 && (
          <DNAAttribute label={dnaLabels.application_role?.label || defaultLabels.application_role.label}>
            <div className="flex flex-wrap gap-2 justify-end">
              {applications.slice(0, 3).map((app, i) => (
                <span 
                  key={i}
                  className="px-2 py-1 bg-white/5 text-white/70 text-[10px] font-medium tracking-wide uppercase"
                >
                  {app}
                </span>
              ))}
            </div>
          </DNAAttribute>
        )}
      </div>
    </section>
  );
};

export default MaterialDNA;
